<script lang="ts">
    import { goto } from "$app/navigation"
    import { movingToTab, selectedTab } from "@store/pageTracker"
    import { cssObserver, resizeEntryBorderBoxBlockSize } from "@utils/directives/useCSSObserver"
    import { isActive, isPage } from "@utils/helpers/is-active"
    import { deviceSize } from "@utils/material/responsive"
    import { notificationData } from "@utils/practice/notifications-data"
    import { notificationsIsCurrentLesson } from "@utils/practice/notification-helpers"
    import {
        NotificationPermissions,
        notificationPermissions,
    } from "@utils/cap-notification-manager/notification-permission"
    import { footerHeight } from "@components/Footer.module"
    import Icon from "@components/material/Icon.svelte"
    import { navigatorOpen } from "@components/navigator/Navigator.svelte"
    import { sideMenuOpen } from "@components/navigator/SideMenu.svelte"
    import PrimaryNavItem from "./PrimaryNavItem.svelte"
    import ReadingTabPanel, {
        collapseReadingTabPanel,
        expandReadingTabPanel,
        readingTabPanelMode,
        toggleReadingTabPanelExpanded,
    } from "./ReadingTabPanel.svelte"

    export let readingMode = false

    $: liveData = $notificationData?.live
    $: practiceBadge =
        $notificationPermissions === NotificationPermissions.GRANTED &&
        !$liveData?.lessonHasPassed &&
        !!$notificationData?.currentLessonItem.readyForReminders &&
        !$notificationsIsCurrentLesson

    $: if ($deviceSize === "desktop") {
        $footerHeight = 0
    }

    function closeSecondaryPanelOnMobile() {
        if ($deviceSize !== "desktop") $navigatorOpen = false
    }

    function handleRouteClick(event: Event, item: "home" | "read" | "practice" | "search") {
        if (item === "read") {
            if ($isActive("/book")) {
                event.preventDefault()
                if ($readingTabPanelMode !== "expanded") $navigatorOpen = false
                toggleReadingTabPanelExpanded()
                return
            }

            if ($selectedTab) {
                event.preventDefault()
                $movingToTab = true
                collapseReadingTabPanel()
                closeSecondaryPanelOnMobile()
                goto("/book/" + $selectedTab.url)
                return
            }

            event.preventDefault()
            closeSecondaryPanelOnMobile()
            expandReadingTabPanel()
            goto("/book")
            return
        }

        collapseReadingTabPanel()
        closeSecondaryPanelOnMobile()
    }

    function openMoreMenu() {
        $sideMenuOpen = true
    }
</script>

<nav class="primary-navigation collapsed" class:reading-mode={readingMode} aria-label="Primary">
    <div
        class="primary-nav-rail"
        use:cssObserver={{
            store: footerHeight,
            fn: (e) => ($deviceSize === "desktop" ? 0 : resizeEntryBorderBoxBlockSize(e)),
        }}
    >
        <div class="brand-block">
            <img class="brand-mark" src="/icons/pwa-512x512.png" alt="" aria-hidden="true" />
            <div class="brand-copy">
                <strong>A Course in Miracles</strong>
                <span>Complete & Annotated Edition</span>
            </div>
        </div>

        <div class="desktop-nav-list">
            <PrimaryNavItem
                href="/"
                icon="home"
                label="Home"
                active={$isPage("/")}
                collapsed
                on:click={(event) => handleRouteClick(event, "home")}
            />
            <PrimaryNavItem
                href="/book"
                icon="book"
                label="Read"
                active={$isActive("/book")}
                collapsed
                on:click={(event) => handleRouteClick(event, "read")}
            />
            <PrimaryNavItem
                href="/search"
                icon="search-thick"
                label="Search"
                active={$isActive("/search")}
                collapsed
                on:click={(event) => handleRouteClick(event, "search")}
            />
            <PrimaryNavItem
                href="/practice"
                icon="calendar"
                label="Practice"
                active={$isActive("/practice")}
                badge={practiceBadge}
                collapsed
                on:click={(event) => handleRouteClick(event, "practice")}
            />
        </div>

        <div class="desktop-tools">
            <button
                type="button"
                class="nav-tool"
                aria-label="Open menu"
                data-tooltip="More"
                on:click={openMoreMenu}
            >
                <span class="tool-icon"><Icon icon="meatballs" /></span>
                <span class="tool-label">More</span>
            </button>
        </div>

        <div class="mobile-bar">
            <PrimaryNavItem
                href="/"
                icon="home"
                label="Home"
                active={$isPage("/")}
                collapsed
                on:click={(event) => handleRouteClick(event, "home")}
            />
            <PrimaryNavItem
                href="/book"
                icon="book"
                label="Read"
                active={$isActive("/book")}
                collapsed
                on:click={(event) => handleRouteClick(event, "read")}
            />
            <PrimaryNavItem
                href="/practice"
                icon="calendar"
                label="Practice"
                active={$isActive("/practice")}
                badge={practiceBadge}
                collapsed
                on:click={(event) => handleRouteClick(event, "practice")}
            />
            <PrimaryNavItem
                href="/search"
                icon="search-thick"
                label="Search"
                active={$isActive("/search")}
                collapsed
                on:click={(event) => handleRouteClick(event, "search")}
            />
            <button
                type="button"
                class="mobile-action"
                aria-label="Open menu"
                on:click={openMoreMenu}
            >
                <span class="mobile-action-icon"><Icon icon="meatballs" /></span>
                <span class="mobile-action-label">More</span>
            </button>
        </div>
    </div>

    {#if readingMode}
        <ReadingTabPanel />
    {/if}
</nav>

<style>
    .primary-navigation {
        --primary-nav-surface: var(--navBackground);
        --primary-nav-mobile-surface: var(--color-grey-0);
        --primary-nav-border: var(--color-purple-100);
        --primary-nav-text: var(--color-grey-600);
        --primary-nav-muted: var(--color-grey-500);
        --primary-nav-strong: var(--color-grey-900);
        --primary-nav-accent: var(--purple);
        --primary-nav-accent-soft: var(--color-purple-50);
        --primary-nav-active-bg: var(--purple);
        --primary-nav-active-text: var(--color-grey-0);
        --primary-nav-tooltip-bg: var(--color-grey-900);
        --primary-nav-tooltip-text: var(--color-grey-0);
        --primary-nav-tooltip-shadow: none;
        --primary-nav-mobile-shadow: none;
        --primary-nav-badge-ring: var(--primary-nav-surface);
        grid-area: primary;
        min-width: 0;
        min-height: 0;
        display: grid;
        grid-template-columns: var(--app-primary-nav-width, 5.25rem);
        grid-template-areas: "rail";
        background: var(--primary-nav-surface);
        color: var(--primary-nav-text);
        position: relative;
        z-index: 20;
    }

    .primary-navigation.reading-mode {
        grid-template-columns: var(--app-primary-nav-width, 5.25rem) var(
                --app-reading-tabs-width,
                0rem
            );
        grid-template-areas: "rail reader-tabs";
    }

    .primary-nav-rail {
        grid-area: rail;
        min-width: 0;
        min-height: 0;
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        gap: 1.5rem;
        container-type: inline-size;
        padding: calc(var(--safe-area-inset-top) + 1rem) 1rem
            calc(var(--safe-area-inset-bottom) + 1rem);
        background: var(--primary-nav-surface);
        border-right: 1px solid var(--primary-nav-border);
        position: relative;
        overflow: visible;
        z-index: 9;
    }

    :global(.darkmode) .primary-navigation {
        --primary-nav-surface: var(--navBackground);
        --primary-nav-mobile-surface: var(--color-grey-900);
        --primary-nav-border: var(--color-grey-800);
        --primary-nav-text: var(--color-grey-300);
        --primary-nav-muted: var(--color-grey-400);
        --primary-nav-strong: var(--color-grey-50);
        --primary-nav-accent: var(--purple);
        --primary-nav-accent-soft: rgb(179 175 212 / 0.14);
        --primary-nav-active-bg: var(--purple);
        --primary-nav-active-text: var(--color-grey-1000);
        --primary-nav-tooltip-bg: var(--color-grey-50);
        --primary-nav-tooltip-text: var(--color-grey-900);
        --primary-nav-tooltip-shadow: none;
        --primary-nav-mobile-shadow: none;
    }

    .brand-block {
        display: grid;
        grid-template-columns: 2.5rem minmax(0, 1fr);
        gap: 0.75rem;
        align-items: center;
        min-width: 0;
    }

    .brand-mark {
        display: block;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 999px;
        object-fit: cover;
    }

    .brand-copy {
        display: grid;
        gap: 0.1rem;
        min-width: 0;
    }

    .brand-copy strong,
    .brand-copy span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .brand-copy strong {
        font-size: 0.95rem;
        line-height: 1.15;
        color: var(--primary-nav-strong);
    }

    .brand-copy span {
        color: var(--primary-nav-muted);
        font-size: 0.72rem;
        line-height: 1.1;
    }

    .desktop-nav-list,
    .desktop-tools {
        display: grid;
        align-content: start;
        gap: var(--spacing-lists);
    }

    .desktop-tools {
        align-content: end;
    }

    .nav-tool,
    .mobile-action {
        appearance: none;
        border: 0;
        box-shadow: none;
        text-transform: none;
        letter-spacing: 0;
        font: inherit;
        cursor: pointer;
        color: var(--primary-nav-text);
        background: transparent;
    }

    .nav-tool {
        display: grid;
        grid-template-columns: 1fr;
        justify-items: center;
        gap: 0.75rem;
        align-items: center;
        position: relative;
        min-height: 2.75rem;
        width: 100%;
        padding: 0.625rem 0.75rem;
        border-radius: 0.5rem;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .nav-tool::after {
        content: attr(data-tooltip);
        position: absolute;
        top: 50%;
        left: calc(100% + 0.75rem);
        min-width: max-content;
        max-width: 12rem;
        padding: 0.42rem 0.7rem;
        border-radius: 999px;
        box-shadow: var(--primary-nav-tooltip-shadow);
        background: var(--primary-nav-tooltip-bg);
        color: var(--primary-nav-tooltip-text);
        font-size: 0.78rem;
        font-weight: 800;
        line-height: 1;
        opacity: 0;
        pointer-events: none;
        transform: translate(0.35rem, -50%);
        transition:
            opacity 0.14s ease,
            transform 0.14s ease;
        white-space: nowrap;
        z-index: 30;
    }

    .nav-tool:hover {
        color: var(--primary-nav-accent);
        background: var(--primary-nav-accent-soft);
    }

    .nav-tool:hover::after,
    .nav-tool:focus-visible::after {
        opacity: 1;
        transform: translate(0, -50%);
    }

    .nav-tool:active {
        transform: translateY(1px);
    }

    .tool-icon {
        width: 1.75rem;
        height: 1.75rem;
        display: grid;
        place-items: center;
    }

    .tool-icon :global(svg) {
        width: 1.15rem;
        height: 1.15rem;
    }

    .tool-label {
        min-width: 0;
        overflow: hidden;
        text-align: left;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.82rem;
        font-weight: 700;
    }

    .mobile-bar {
        display: none;
    }

    .primary-navigation.collapsed .brand-block,
    .primary-navigation.collapsed .nav-tool {
        grid-template-columns: 1fr;
        justify-items: center;
    }

    .primary-navigation.collapsed .brand-copy,
    .primary-navigation.collapsed .tool-label {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    @container (max-width: 6rem) {
        .brand-block,
        .nav-tool {
            grid-template-columns: 1fr;
            justify-items: center;
        }

        .brand-copy,
        .tool-label {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            white-space: nowrap;
        }
    }

    @media only screen and (max-width: 1153px) {
        .primary-navigation,
        .primary-navigation.collapsed {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: auto;
            grid-template-areas: "rail";
            background: transparent;
        }

        .primary-navigation.reading-mode {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: auto auto;
            grid-template-areas: "reader-tabs" "rail";
        }

        .primary-nav-rail,
        .primary-navigation.collapsed .primary-nav-rail {
            display: block;
            padding: 0.4rem 0.5rem max(0.45rem, var(--safe-area-inset-bottom));
            min-height: calc(4.75rem + var(--safe-area-inset-bottom));
            border-right: 0;
            border-top: 1px solid var(--primary-nav-border);
            background: var(--primary-nav-mobile-surface);
            box-shadow: var(--primary-nav-mobile-shadow);
        }

        .brand-block,
        .desktop-nav-list,
        .desktop-tools {
            display: none;
        }

        .mobile-bar {
            display: grid;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: 0.18rem;
            height: 100%;
            align-items: center;
        }

        .mobile-action {
            display: grid;
            grid-template-rows: 1.8rem 1rem;
            align-content: center;
            align-items: center;
            justify-items: center;
            gap: 0.16rem;
            min-width: 0;
            min-height: 3.75rem;
            padding: 0;
            border-radius: 0.5rem;
            color: var(--primary-nav-muted);
            font-size: 0.68rem;
            line-height: 1;
            font-weight: 700;
            transition:
                background-color 0.16s ease,
                color 0.16s ease;
        }

        .mobile-action:hover {
            color: var(--primary-nav-accent);
            background: var(--primary-nav-accent-soft);
        }

        .mobile-action-icon {
            width: 1.8rem;
            height: 1.8rem;
            display: grid;
            place-items: center;
        }

        .mobile-action-icon :global(svg) {
            width: 1.18rem;
            height: 1.18rem;
        }

        .mobile-action-label {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 1;
        }
    }

    @media only screen and (max-width: 420px) {
        .mobile-bar {
            gap: 0.08rem;
        }
    }

    @media only screen and (max-width: 360px) {
        .mobile-action {
            min-height: 3.45rem;
            font-size: 0.62rem;
            gap: 0.08rem;
        }
    }
</style>
